import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import BrandLogo from "@/components/BrandLogo";
import { toast } from "sonner";
import { Gift, CheckCircle2, ArrowRight } from "lucide-react";
import { PublicFooter, PrivacyConsent } from "@/components/PublicFooter";

export default function Affiliation() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await register(form.name, form.email, form.password, { affiliate_only: true });
      toast.success("Compte affilié créé. Voici votre tableau de bord de parrainage.");
      navigate("/parrainage");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  return (
    <>
    <div className="min-h-screen grid lg:grid-cols-2" data-testid="affiliation-signup-page">
      <div className="hidden lg:flex flex-col justify-between p-14 relative overflow-hidden" style={{ background: "linear-gradient(150deg, #1A1614 0%, #2A211E 100%)" }}>
        <BrandLogo dark />
        <div className="relative z-10 max-w-md text-white">
          <div className="inline-flex items-center gap-2 bg-coral-strong/20 text-coral text-xs font-bold uppercase tracking-[0.15em] px-3 py-1.5 rounded-full mb-6"><Gift className="h-3.5 w-3.5" /> Programme de parrainage</div>
          <h1 className="font-display font-extrabold text-4xl leading-[1.1] mb-6">Recommandez Pulse. Soyez récompensé.</h1>
          <ul className="space-y-3 text-white/70">
            <li className="flex gap-2"><CheckCircle2 className="h-5 w-5 text-coral shrink-0" /> Compte 100% gratuit, sans engagement.</li>
            <li className="flex gap-2"><CheckCircle2 className="h-5 w-5 text-coral shrink-0" /> Commission sur chaque client parrainé (50% la 1re année).</li>
            <li className="flex gap-2"><CheckCircle2 className="h-5 w-5 text-coral shrink-0" /> Ouvert à tous : Membres, consultants, prescripteurs.</li>
            <li className="flex gap-2"><CheckCircle2 className="h-5 w-5 text-coral shrink-0" /> Passez à l'essai ou à un abonnement quand vous voulez, sans perdre votre historique.</li>
          </ul>
        </div>
        <p className="text-white/40 text-xs relative z-10">Pulse, by APOLLO</p>
        <div className="absolute -right-32 -bottom-32 w-[28rem] h-[28rem] rounded-full bg-coral/20 blur-3xl" />
      </div>

      <div className="flex items-center justify-center p-8 bg-background">
        <div className="w-full max-w-sm">
          <div className="lg:hidden mb-8"><BrandLogo /></div>
          <h2 className="font-display font-bold text-3xl text-ink mb-1">Devenir affilié</h2>
          <p className="text-slate-500 text-sm mb-8">Compte gratuit, accès au tableau de bord de parrainage.</p>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">Nom</label>
              <input data-testid="affiliate-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="mt-1.5 w-full border border-slate-200 rounded-sm px-3.5 py-2.5 text-sm outline-none focus:ring-1 focus:ring-coral bg-white" placeholder="Gilles Martin" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">E-mail</label>
              <input data-testid="affiliate-email-input" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="mt-1.5 w-full border border-slate-200 rounded-sm px-3.5 py-2.5 text-sm outline-none focus:ring-1 focus:ring-coral bg-white" placeholder="vous@exemple.fr" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">Mot de passe</label>
              <input data-testid="affiliate-password-input" type="password" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="mt-1.5 w-full border border-slate-200 rounded-sm px-3.5 py-2.5 text-sm outline-none focus:ring-1 focus:ring-coral bg-white" placeholder="••••••••" />
            </div>
            <button type="submit" disabled={busy} data-testid="affiliate-submit-btn"
              className="w-full flex items-center justify-center gap-2 bg-coral-strong text-white py-3 rounded-sm text-sm font-semibold hover:bg-coral-hover transition-colors disabled:opacity-60">
              {busy ? "Patientez…" : <>Créer mon compte affilié <ArrowRight className="h-4 w-4" /></>}
            </button>
            <PrivacyConsent />
          </form>
          <p className="text-sm text-slate-500 mt-6 text-center">
            Déjà inscrit ? <Link to="/login" className="text-coral-dark font-semibold hover:underline">Se connecter</Link>
          </p>
        </div>
      </div>
    </div>
    <PublicFooter />
    </>
  );
}
